import { useEffect, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Plus, Tag, Pencil, Trash2, Sparkles } from "lucide-react";
import DiscountFormSheet, { type DiscountRow } from "@/components/admin/DiscountFormSheet";
import VipDiscountSheet from "@/components/admin/VipDiscountSheet";

const AdminDiscountsPage = () => {
  const [rows, setRows] = useState<DiscountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [vipSheetOpen, setVipSheetOpen] = useState(false);
  const [editing, setEditing] = useState<DiscountRow | null>(null);

  const fetchData = async () => {
    setLoading(true);
    const { data, error } = await (supabase.from as any)("discounts")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Kunde inte hämta rabatter");
      console.error(error);
    } else {
      setRows((data ?? []) as DiscountRow[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleNew = () => {
    setEditing(null);
    setSheetOpen(true);
  };

  const handleEdit = (row: DiscountRow) => {
    setEditing(row);
    setSheetOpen(true);
  };

  const handleDelete = async (row: DiscountRow) => {
    if (!confirm(`Ta bort rabatten "${row.name}"?`)) return;
    // Remove the coupon at Stripe first — after the row is gone we can no
    // longer tell which coupon belonged to it.
    const { error: syncErr } = await supabase.functions.invoke("sync-discount-stripe", {
      body: { discount_id: row.id, mode: "remove" },
    });
    if (syncErr) console.error(syncErr);
    const { error } = await (supabase.from as any)("discounts").delete().eq("id", row.id);
    if (error) {
      toast.error("Kunde inte ta bort");
      console.error(error);
    } else {
      toast.success("Rabatt borttagen");
      fetchData();
    }
  };

  const formatValue = (row: DiscountRow) => {
    if (row.discount_type === "percent") return `−${row.value}%`;
    return `−${(row.value / 100).toFixed(0)} kr`;
  };

  const formatStatus = (row: DiscountRow) => {
    if (!row.active) return <Badge variant="secondary">Inaktiv</Badge>;
    if (row.expires_at && new Date(row.expires_at) < new Date()) {
      return <Badge variant="destructive">Utgången</Badge>;
    }
    if (row.max_redemptions && row.times_redeemed >= row.max_redemptions) {
      return <Badge variant="destructive">Slut</Badge>;
    }
    return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Aktiv</Badge>;
  };

  const formatScope = (row: DiscountRow) => {
    const parts: string[] = [];
    if (row.applies_to === "all") parts.push("alla");
    if (row.applies_to === "purchase") parts.push("köp");
    if (row.applies_to === "subscription") parts.push("prenumeration");
    if (row.allowed_tiers && row.allowed_tiers.length > 0) {
      parts.push(`tuber: ${row.allowed_tiers.join(", ")}`);
    } else if (row.min_tubes) {
      parts.push(`min ${row.min_tubes} tuber`);
    }
    return parts.join(" · ");
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Tag className="h-6 w-6" />
          <div>
            <h1 className="text-2xl font-semibold">Rabatter & kampanjer</h1>
            <p className="text-sm text-muted-foreground">
              Hantera rabattkoder, tidsbegränsade kampanjer och VIP-priser
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setVipSheetOpen(true)}>
            <Sparkles className="h-4 w-4 mr-2" />
            VIP-priser
          </Button>
          <Button onClick={handleNew}>
            <Plus className="h-4 w-4 mr-2" />
            Ny rabatt
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Tag className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Inga rabatter än</p>
              <Button variant="link" onClick={handleNew}>
                Skapa din första rabatt
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kod / Namn</TableHead>
                  <TableHead>Värde</TableHead>
                  <TableHead>Omfattning</TableHead>
                  <TableHead>Använd</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <div className="font-mono text-sm font-semibold">
                        {row.code ?? <span className="italic text-muted-foreground">VIP (auto)</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">{row.name}</div>
                    </TableCell>
                    <TableCell className="font-semibold">{formatValue(row)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatScope(row)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {row.times_redeemed}
                      {row.max_redemptions && (
                        <span className="text-muted-foreground"> / {row.max_redemptions}</span>
                      )}
                    </TableCell>
                    <TableCell>{formatStatus(row)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(row)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(row)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <DiscountFormSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        initial={editing}
        onSaved={fetchData}
      />

      <VipDiscountSheet open={vipSheetOpen} onOpenChange={setVipSheetOpen} />
    </AdminLayout>
  );
};

export default AdminDiscountsPage;
